/* eslint-disable no-restricted-globals */
import Router, { SingletonRouter } from "next/router";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { IntlShape, defineMessages, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { ResponseData as PassengerNotificationResponseData } from "shared/cbt/api/fetchPassengerNotification";
import ResultContainer from "shared/containers/Result/ResultContainer";
import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import { AppState } from "shared/data/reducers";
import { User } from "shared/data/reducers/userReducer";
import { scrollTo } from "shared/lib/functions";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
import { formattedMessageParams } from "shared/messages";

import BookingForm from "@components/BookingForm/BookingForm";
import BookingFormSubmit from "@components/BookingForm/BookingFormSubmit";
import BrandsModal from "@components/Flights/BrandedFares/BrandsModal";
import ChangeTicket from "@components/Flights/ChangeTicket";
import FlightAdditionalInfo from "@components/Flights/FlightAdditionalInfo";
import pageWithIntl from "@components/PageWithIntl";
import BookingFormTips from "@components/Result/BookingFormTips";
import PassengerNotificationsModal from "@components/Result/PassengerNotificationsModal";
import ResultFlightInfo from "@components/Result/ResultFlightInfo";
import ResultLogin from "@components/Result/ResultLogin";
import ResultPromoCodes from "@components/Result/ResultPromoCodes";
import ResultRecapitulation from "@components/Result/ResultRecapitulation";
import FormattedMessage from "@components/UI/FormattedMessage";
import SearchSteps from "@components/UI/Header/SearchSteps";
import SearchStepsMobile from "@components/UI/Header/SearchStepsMobile";
import { LegacyModalAlert } from "@components/UI/useModalAlert";
import PageSeo from "@components/Utils/PageSeo";

import { pageDataLayer } from "@lib/dataLayers";
import {
  errorCodesToRedirect,
  formatPassengerRecapitulation,
  routeChangeFallback,
  scrollToErrorDynamicForm,
} from "@lib/webFunctions";

import useShareInfo from "../hooks/result/useShareInfo";

// @ts-ignore
const Logger = require("shared/services/Logger");

const { title, brandOfferNotAvailable } = defineMessages({
  title: formattedMessageParams("Result.title"),
  brandOfferNotAvailable: formattedMessageParams(
    "BrandedFares.offerNotAvailable"
  ),
});

interface Props {
  dispatch: Dispatch<any>;
  cbtApiUrl: string;
  customerUsername: string;
  intl: IntlShape;
  router: SingletonRouter;
  currentLanguage: string;
  currency: string;
  bookingForm: any;
  forms: any;
  passengerNotificationMessages: {
    isLoading: boolean;
    messages: PassengerNotificationResponseData[];
  };
  user: User;
  actualFlightOffer: any;
  frontendSettings: any;
  isLoggedIn: boolean;
  fullPrice: any;
  refs: any;
  getPriceWithPromoCode: any;
  componentDidFocus: () => Promise<void>;
  backgroundImageUrl?: string;
}

const ShareInfoWrapper = ({ children }) => {
  const shareInfo = useShareInfo();
  return children(shareInfo);
};

// @ts-ignore
const Result = forwardRef((props: Props, ref) => {
  const {
    intl,
    router,
    frontendSettings,
    isLoggedIn,
    dispatch,
    passengerNotificationMessages,
    user,
    actualFlightOffer,
    currency,
    componentDidFocus,
    fullPrice,
    refs,
    getPriceWithPromoCode,
    currentLanguage,
    backgroundImageUrl,
  } = props;

  const [state, setState] = useState({
    actualFlightOffer: {},
    ticketingCombinations: [],
    paymentFormMethods: [],
    submitted: false,
    showTermAndConditionsModal: false,
    passengerParams: [],
    customerDetail: {},
    initialPassengerValues: null,
    showVerticalScrollBar: false,
    showHorizontalScrollBar: false,
    showBrandModal: false,
    formData: {
      billing: {
        dic: "",
        ic: "",
        company: "",
        street: "",
        country: "",
        zip: "",
        city: "",
      },
    },
    billingActive: false,
    errorMsg: null,
    errorCode: null,
    customErrorMessage: null,
    showPassengerNotificationModal: !!passengerNotificationMessages.messages
      .length,
  });

  // Handlers
  const setErrorCode = (errorCode: string) => {
    setState((prev) => ({ ...prev, errorCode }));
  };

  const showRawError = (errorMsg: string) => {
    setState((prev) => ({ ...prev, errorMsg }));
  };

  const showCustomError = (messageId: string) => {
    setState((prev) => ({
      ...prev,
      customErrorMessage: intl.formatMessage({ id: messageId }),
    }));
  };

  const hideCustomError = () => {
    setState((prev) => ({ ...prev, customErrorMessage: null }));
    goBack();
  };

  const hidePassengerNotification = () => {
    setState((prev) => ({
      ...prev,
      showPassengerNotificationModal: false,
    }));
  };

  const goBack = () => {
    if (state.errorCode && !errorCodesToRedirect.includes(state.errorCode)) {
      setState((prev) => ({
        ...prev,
        errorCode: null,
        customErrorMessage: null,
      }));
      return;
    }

    routeChangeFallback(() => {
      router.push("/");
    });

    setState((prev) => ({
      ...prev,
      errorCode: null,
      customErrorMessage: null,
    }));
    router.back();
  };

  const getRefs = () => {
    const qd = {};
    if (location.search)
      location.search
        .substring(1)
        .split(`&`)
        .forEach((item) => {
          // eslint-disable-next-line prefer-const
          let [k, v] = item.split(`=`);
          v = v && decodeURIComponent(v);
          (qd[k] = qd[k] || []).push(v);
        });
    const { refs: queryRefs } = qd as { refs?: string[] };
    if (queryRefs === undefined || queryRefs === null) {
      return [];
    }

    return queryRefs.map((reference: string) => decodeURIComponent(reference));
  };

  const navigateToPayment = (paymentUrl) => {
    window.location.href = paymentUrl;
  };

  const navigateToBooked = (reservationEmail, reservationId) => {
    Router.push({
      pathname: "/booked",
      query: {
        status: "success",
        reservationEmail,
        reservationId,
        ...(Router.router.query.lang ? { lang: Router.router.query.lang } : {}),
      },
    });
  };

  const scrollToError = () => {
    scrollToErrorDynamicForm();
  };

  const showNotFoundError = () => {
    setState((prev) => ({
      ...prev,
      customErrorMessage: intl.formatMessage({
        id: "Alert.flightNotFound",
      }),
    }));

    dispatch(stopUniversalLoading());
  };

  const showBrandModal = () => {
    setState((prev) => ({ ...prev, showBrandModal: true }));
  };

  const closeErrorModal = () => {
    setState((prev) => ({ ...prev, errorMsg: null }));
    dispatch(stopUniversalLoading());
    goBack();
  };

  useImperativeHandle(ref, () => ({
    getRefs,
    showCustomError,
    showBrandModal,
    showNotFoundError,
    showRawError,
    setErrorCode,
    navigateToPayment,
    navigateToBooked,
    scrollToError,
  }));

  // ComponentDidMount
  useEffect(() => {
    const initPage = async () => {
      pageDataLayer("services", user);
      pageDataLayer("customerInfo", user);

      dispatch(startUniversalLoading());
      await componentDidFocus();

      try {
        Router.prefetch("/booked");

        const SCROLL_TO_CONTENT_TIMEOUT_MS = 40;
        setTimeout(
          () => scrollTo(".search-steps-container"),
          SCROLL_TO_CONTENT_TIMEOUT_MS
        );

        dispatch(stopUniversalLoading());
      } catch (e) {
        Logger.error(e);
        dispatch(stopUniversalLoading());
      }
    };

    initPage();
  }, [dispatch, user, componentDidFocus]);

  useEffect(() => {
    if (
      passengerNotificationMessages.messages.length > 0 &&
      !state.showPassengerNotificationModal
    ) {
      setState((prev) => ({
        ...prev,
        showPassengerNotificationModal: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengerNotificationMessages.messages]);

  // Render
  const bookingDisabled =
    frontendSettings?.dealerCorporateSettings?.enableAnonymousBook ===
      "false" && !isLoggedIn;

  const passengerChangeConditions = actualFlightOffer.TicketChangeConditions
    ? actualFlightOffer.TicketChangeConditions.PassengerChangeConditions
    : actualFlightOffer.PricingDetails?.PricingDetail?.[0]
        ?.TicketChangeConditions?.PassengerChangeConditions || false;

  if (state.showBrandModal && !actualFlightOffer.FlightItinerary) {
    return (
      <LegacyModalAlert
        show={state.showBrandModal}
        onClose={() => {
          setState((prev) => ({ ...prev, showBrandModal: false }));

          const { query } = Router;
          delete query.refs;

          return Router.push({
            pathname: "/results",
            query,
          });
        }}
        title={intl.formatMessage(brandOfferNotAvailable)}
        customTitleClass="branded-fares -error"
        hideButtons
        isCloseButtonHidden={false}
        customContent={
          <BrandsModal
            getRefsForFlightOffer={getRefs}
            flightOffer={actualFlightOffer}
            currency={currency}
            keepBookingClass={false}
          />
        }
      />
    );
  }

  if (!actualFlightOffer.FlightItinerary) {
    return (
      <LegacyModalAlert
        show={!!state.customErrorMessage}
        onClose={goBack}
        message={state.customErrorMessage}
        hideHeader
      />
    );
  }

  const flightStreams = actualFlightOffer.FlightItinerary.FlightStream;

  const formattedPassengerPrices = formatPassengerRecapitulation(
    actualFlightOffer?.AlternativeCurrencyPricingDetails
      ?.AlternativeCurrencyPricingDetail?.[0].FlightPricing
      ?.FlightPassengerPrices?.FlightPassengerPrice ||
      actualFlightOffer?.FlightPassengerPrices?.FlightPassengerPrice ||
      actualFlightOffer.FlightPassengerPrices
  );

  return (
    <div>
      <PageSeo
        title={{
          defaultTitle: intl.formatMessage(title),
        }}
      />
      <div className="search-steps-container">
        <SearchSteps page="result" />
      </div>
      <div className="search-steps-mobile-container">
        <SearchStepsMobile page="result" />
      </div>
      <div className="container">
        {actualFlightOffer.Warnings?.Warning?.length > 0 && (
          <div className="result-warning">
            {actualFlightOffer.Warnings.Warning[0].$t}
          </div>
        )}
        <div className="result" id="result-main-div">
          <div className="result-information">
            <div className={backgroundImageUrl ? "result-box-description" : ""}>
              <h3 className="h3 result-h3">
                <FormattedMessage id="Result.flightInformation" />
              </h3>
              {actualFlightOffer.CombinedOffer === "true" && (
                <span
                  id="two-one-way-detail-text"
                  style={{
                    display: "inline-block",
                    marginLeft: "10px",
                    marginBottom: "15px",
                  }}
                >
                  <FormattedMessage id="FlightOffer.OneWayIndicator.tooltip" />
                </span>
              )}
              {actualFlightOffer.LowCost === "true" && (
                <span
                  id="low-cost-detail-text"
                  style={{
                    display: "inline-block",
                    marginLeft: "10px",
                    marginBottom: "15px",
                  }}
                >
                  <FormattedMessage id="FlightOffer.LowCostIndicator.tooltip" />
                </span>
              )}
            </div>
            <div className="result-box" style={{ paddingTop: "35px" }}>
              {flightStreams.map((flightStream, index) => (
                <ResultFlightInfo
                  key={index}
                  index={index}
                  flightOption={flightStream.FlightOption}
                  currentLanguage={currentLanguage}
                />
              ))}
            </div>
            <ShareInfoWrapper>
              {(shareInfo) => (
                <FlightAdditionalInfo
                  selectedCombination={{
                    PricingDetail: {
                      BaggageLimit: {
                        DetailsAvailable:
                          router.query?.detailsAvailable ?? null,
                        Quantity: router.query?.baggageLimitQuantity ?? null,
                        Weight: router.query?.baggageLimitWeight ?? null,
                        Included: router.query?.baggageIncluded ?? null,
                      },
                    },
                  }}
                  index={0}
                  getRefsForFlightOffer={getRefs}
                  intl={intl}
                  flightSource={router.query?.flightSource ?? null}
                  page="result"
                  shareInfo={shareInfo}
                />
              )}
            </ShareInfoWrapper>
            <div
              className={
                backgroundImageUrl
                  ? "result-form-box-wrapper only-mobile"
                  : "only-mobile"
              }
              id="result-summary-container-mobile"
            >
              <ResultRecapitulation
                passengerPrices={formattedPassengerPrices}
                backgroundImageUrl={backgroundImageUrl}
              />
            </div>
            {passengerChangeConditions && (
              <ChangeTicket
                passengerChangeConditions={passengerChangeConditions}
              />
            )}
            {bookingDisabled ? (
              <ResultLogin />
            ) : (
              <>
                <BookingFormTips backgroundImageUrl={backgroundImageUrl} />

                <BookingForm
                  refs={refs}
                  fullPrice={fullPrice}
                  isFlight
                  isHotel={false}
                />

                <div className="only-mobile">
                  <ResultPromoCodes
                    getPriceWithPromoCode={getPriceWithPromoCode}
                    destination="mobile"
                  />
                </div>
              </>
            )}
          </div>
          <div className="result-summary" id="result-summary-container">
            <ResultRecapitulation
              passengerPrices={formattedPassengerPrices}
              backgroundImageUrl={backgroundImageUrl}
            />
            <ResultPromoCodes
              getPriceWithPromoCode={getPriceWithPromoCode}
              destination="desktop"
            />
          </div>
        </div>

        {!bookingDisabled && (
          <BookingFormSubmit refs={refs} fullPrice={fullPrice} />
        )}
      </div>

      <LegacyModalAlert
        show={state.errorMsg !== null}
        onClose={closeErrorModal}
        message={state.errorMsg}
        hideHeader
      />
      <LegacyModalAlert
        show={!!state.customErrorMessage}
        onClose={hideCustomError}
        message={state.customErrorMessage}
        hideHeader
      />
      <PassengerNotificationsModal
        show={!!state.showPassengerNotificationModal}
        onClose={hidePassengerNotification}
        notifications={passengerNotificationMessages.messages}
      />
    </div>
  );
});

// @ts-ignore
Result.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error("Error Initial. ", e);
    return {};
  }
};

const mapStateToProps = (state: AppState) => ({
  fileServerData: state.requestorConfig.fileServerData,
  textStorage: state.requestorConfig.textStorage,
  passengerPrefixes: state.storage.passengerPrefix,
  forms: state.forms,
  isLoggedIn: state.user.isLoggedIn,
  user: state.user,
  currency: state.price.currency,
  backgroundImageUrl:
    state.storage.frontendSettings.dealerFrontendSettings?.background?.image
      ?.url,
});

// @ts-ignore - Ignoring complex type issues in HOC chain
export default connect(mapStateToProps)(
  pageWithIntl(
    ResultContainer(injectIntl(Result, { forwardRef: true }), connect)
  )
);
